"use client"

import { useState, useEffect, useMemo, useCallback, useRef } from "react"
import { usePrerenderSafeSearchParams } from "@/hooks/use-prerender-safe-search-params"
import {
  calculateLawnCosts,
  type LawnCostResult,
} from "@/lib/cost-calculator"
import { useSavedInputs } from "@/hooks/use-saved-inputs"
import {
  getRegionFromZipCode,
  REGIONAL_CLIMATE_DATA,
  REGION_DEFAULT_GRASS,
  toLegacyRegion,
} from "@/data/regional-climate"
import { GRASS_CHARACTERISTICS } from "@/data/grass-characteristics"
import { ZipCodeInput } from "@/components/ui/zip-code-input"
import { grassDisplayName } from "@/lib/grass-display"

// ── Tokens — neutral palette to match the rest of the site.
const COST_BG = "#ffffff"
const COST_INK = "#111827" // gray-900
const COST_RULE = "#e5e7eb" // gray-200
const COST_ACCENT = "#15803d" // green-700, matches site CTA
const COST_PAPER_DEEP = "#f3f4f6" // gray-100
const COST_MUTED = "rgba(17,24,39,0.62)"
const COST_MUTED_2 = "rgba(17,24,39,0.45)"

// ── Engine ↔ design ID maps ────────────────────────────────────────────
type Approach = "diy" | "professional" | "hybrid"
type Level = "basic" | "standard" | "premium"
type Season = "warm" | "cool" | "trans"

interface CostFormProps {
  initialValues?: {
    zipCode?: string
    grassType?: string
    sunlight?: string
    lawnSize?: number
  }
  /** Called whenever the live calculated result changes, so the page can render
   *  the result card + breakdown without a submit step. Null until the ZIP
   *  resolves to a known region (the only required input with no default). */
  onResultChange?: (
    result: LawnCostResult | null,
    inputs?: { includeEquipment: boolean; includeWater: boolean },
  ) => void
  error?: string | null
}

// ── Inline icons ───────────────────────────────────────────────────────
function Ico({ size = 16, children }: { size?: number; children: React.ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  )
}
const IcoLawn = (p: { size?: number }) => <Ico {...p}><path d="M4 20h16" /><path d="M7 20c0-4 1-7 3-9" /><path d="M12 20c0-5 1-8 3-10" /><path d="M17 20c0-3 0-6 2-8" /></Ico>
const IcoPin = (p: { size?: number }) => <Ico {...p}><path d="M12 21s-7-7.5-7-12a7 7 0 0114 0c0 4.5-7 12-7 12z" /><circle cx="12" cy="9" r="2.5" /></Ico>
const IcoLeaf = (p: { size?: number }) => <Ico {...p}><path d="M5 19c8 0 14-6 14-14C9 5 5 11 5 19z" /><path d="M5 19l9-9" /></Ico>
const IcoCog = (p: { size?: number }) => <Ico {...p}><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.7 1.7 0 00.3 1.8l.1.1a2 2 0 11-2.8 2.8l-.1-.1a1.7 1.7 0 00-1.8-.3 1.7 1.7 0 00-1 1.5V21a2 2 0 11-4 0v-.1a1.7 1.7 0 00-1.1-1.5 1.7 1.7 0 00-1.8.3l-.1.1A2 2 0 114.3 17l.1-.1a1.7 1.7 0 00.3-1.8 1.7 1.7 0 00-1.5-1H3a2 2 0 110-4h.1A1.7 1.7 0 004.6 9a1.7 1.7 0 00-.3-1.8l-.1-.1A2 2 0 117 4.3l.1.1a1.7 1.7 0 001.8.3H9a1.7 1.7 0 001-1.5V3a2 2 0 114 0v.1a1.7 1.7 0 001 1.5 1.7 1.7 0 001.8-.3l.1-.1A2 2 0 1119.7 7l-.1.1a1.7 1.7 0 00-.3 1.8V9c.2.6.8 1 1.5 1H21a2 2 0 110 4h-.1a1.7 1.7 0 00-1.5 1z" /></Ico>
const IcoWrench = (p: { size?: number }) => <Ico {...p}><path d="M14.7 6.3a4 4 0 105.6 5.6L21 11l-3-3 1.3-1.3a4 4 0 00-4.6 0z" /><path d="M14 8L4 18l2 2 10-10" /></Ico>
const IcoCheck = (p: { size?: number }) => <Ico {...p}><path d="M5 12l4 4 10-10" /></Ico>
const IcoLock = (p: { size?: number }) => <Ico {...p}><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V7a4 4 0 018 0v4" /></Ico>
const IcoEdit = (p: { size?: number }) => <Ico {...p}><path d="M4 20h4l10-10-4-4L4 16v4z" /><path d="M14 6l4 4" /></Ico>

// ── Static data ────────────────────────────────────────────────────────
const SIZE_PRESETS: Array<{ value: number; label: string; note: string }> = [
  { value: 1500, label: "Small", note: "townhouse / patio" },
  { value: 5000, label: "Typical", note: "suburban lot" },
  { value: 10000, label: "Large", note: "¼-acre lot" },
  { value: 22000, label: "Half ac", note: "half-acre +" },
]

const SEASONS: Array<{ id: Season; label: string; note: string; recommend: string }> = [
  { id: "warm", label: "Warm-season", note: "Southeast, TX, S. CA", recommend: "Bermudagrass" },
  { id: "cool", label: "Cool-season", note: "NE, Midwest, PNW", recommend: "Tall Fescue" },
  { id: "trans", label: "Transition", note: "NC → MO → KS", recommend: "Zoysiagrass" },
]

const GRASSES_BY_SEASON: Record<Season, string[]> = {
  warm: ["Bermudagrass", "Zoysiagrass", "St. Augustinegrass", "Centipede", "Bahiagrass", "Kikuyugrass"],
  cool: ["Tall Fescue", "Kentucky Bluegrass", "Perennial Ryegrass", "Fine Fescue", "Annual Ryegrass", "Mixed Cool-Season Blend"],
  trans: ["Zoysiagrass", "Tall Fescue", "Bermudagrass", "Kentucky Bluegrass"],
}

const LEVELS: Array<{ id: Level; name: string; pitch: string; incl: string[]; recommended?: boolean }> = [
  { id: "basic", name: "Basic", pitch: "Mow + fertilize twice a year.", incl: ["4 mowings/mo", "Spring + fall feed", "No weed control"] },
  { id: "standard", name: "Standard", pitch: "A typical care plan most lawns get.", incl: ["Weekly mow", "4-step fertilizer", "Pre + post-emergent"], recommended: true },
  { id: "premium", name: "Premium", pitch: "Full program for a magazine-cover lawn.", incl: ["Weekly mow + edge", "6-step program", "Aerate + overseed", "Grub + insect"] },
]

const APPROACHES: Array<{ id: Approach; name: string; meta: string; blurb: string }> = [
  { id: "diy", name: "Do it yourself", meta: "Time: ~3 hr/week", blurb: "You buy the inputs, you do the work." },
  { id: "hybrid", name: "Hybrid", meta: "Time: ~1 hr/week", blurb: "You mow. A pro handles treatments." },
  { id: "professional", name: "Hire a pro", meta: "Time: ~0 hr/week", blurb: "They show up. You enjoy the lawn." },
]

const REGION_TO_SEASON: Record<string, Season> = {
  Southeast: "warm",
  "South Central": "warm",
  Northeast: "cool",
  "Great Lakes": "cool",
  "Upper Midwest": "cool",
  "Mountain West": "cool",
  "Mid-Atlantic": "trans",
  Central: "trans",
  "West Coast": "trans",
}

// ── Reusable bits ──────────────────────────────────────────────────────
function StepCircle({ step, accent }: { step: number; accent?: boolean }) {
  return (
    <span
      aria-hidden="true"
      style={{
        width: 27,
        height: 27,
        flexShrink: 0,
        borderRadius: "50%",
        background: COST_PAPER_DEEP,
        border: accent ? `1.5px solid ${COST_ACCENT}` : `1px solid ${COST_RULE}`,
        boxShadow: accent ? `0 0 0 3px ${COST_ACCENT}22` : "none",
        color: accent ? COST_ACCENT : COST_INK,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 13,
        fontWeight: 600,
        lineHeight: 1,
      }}
    >
      {step}
    </span>
  )
}

function Label({
  children,
  hint,
  step,
  accent,
}: {
  children: React.ReactNode
  hint?: React.ReactNode
  step?: number
  accent?: boolean
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        marginBottom: 10,
        columnGap: 12,
        rowGap: 4,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0, flex: "0 1 auto" }}>
        {step != null && <StepCircle step={step} accent={accent} />}
        <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.14em", textTransform: "uppercase", color: COST_INK, opacity: 0.55 }}>
          {children}
        </div>
      </div>
      {hint && (
        <div
          style={{
            flex: "1 1 150px",
            minWidth: 0,
            fontSize: 12,
            color: COST_MUTED,
            textAlign: "right",
          }}
        >
          {hint}
        </div>
      )}
    </div>
  )
}

function SectionCard({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        background: "#fff",
        border: `1px solid ${COST_RULE}`,
        borderRadius: 12,
        padding: 18,
      }}
    >
      {children}
    </div>
  )
}

// ── Lawn size field ────────────────────────────────────────────────────
function SizeField({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(String(value))
  useEffect(() => { if (!editing) setDraft(String(value)) }, [value, editing])

  const min = 200, max = 30000
  const toSlider = (v: number) =>
    Math.round(((Math.log(v) - Math.log(min)) / (Math.log(max) - Math.log(min))) * 1000)
  const fromSlider = (s: number) =>
    Math.round(Math.exp(Math.log(min) + (s / 1000) * (Math.log(max) - Math.log(min))) / 50) * 50

  const acres = (value / 43560).toFixed(value < 5000 ? 3 : 2)

  return (
    <div>
      <Label step={1}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <IcoLawn size={13} /> Lawn size
        </span>
      </Label>

      <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
        {editing ? (
          <input
            autoFocus
            inputMode="numeric"
            value={draft}
            onChange={(e) => setDraft(e.target.value.replace(/[^0-9]/g, ""))}
            onBlur={() => {
              const n = Math.max(min, Math.min(max, parseInt(draft || "0", 10)))
              onChange(n)
              setEditing(false)
            }}
            onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur() }}
            aria-label="Lawn size in square feet"
            style={{
              fontSize: 36, fontWeight: 600, color: COST_INK,
              fontVariantNumeric: "tabular-nums",
              border: "none", borderBottom: `2px solid ${COST_ACCENT}`,
              background: "transparent", width: 180, padding: "2px 0",
              letterSpacing: "-0.01em",
            }}
          />
        ) : (
          <button
            type="button"
            onClick={() => setEditing(true)}
            aria-label={`Lawn size ${value} square feet, click to edit`}
            style={{
              display: "inline-flex", alignItems: "baseline", gap: 8,
              background: "transparent", border: "none", padding: 0,
              cursor: "text", color: COST_INK, textAlign: "left",
            }}
          >
            <span style={{ fontSize: 36, fontWeight: 600, lineHeight: 1, letterSpacing: "-0.01em", fontVariantNumeric: "tabular-nums" }}>
              {value.toLocaleString()}
            </span>
            <span style={{ fontSize: 13, color: COST_MUTED }}>sq ft</span>
            <IcoEdit size={14} />
          </button>
        )}
        <span style={{ marginLeft: "auto", fontSize: 12, color: COST_MUTED, fontVariantNumeric: "tabular-nums" }}>
          ≈ {acres} ac
        </span>
      </div>

      <div style={{ position: "relative", padding: "6px 0 14px" }}>
        <input
          type="range"
          min={0}
          max={1000}
          value={toSlider(value)}
          onChange={(e) => onChange(fromSlider(parseInt(e.target.value, 10)))}
          aria-label="Lawn size slider"
          className="cost-range"
          style={{
            width: "100%", height: 6, WebkitAppearance: "none", appearance: "none",
            background: `linear-gradient(to right, ${COST_ACCENT} 0%, ${COST_ACCENT} ${toSlider(value) / 10}%, ${COST_RULE} ${toSlider(value) / 10}%, ${COST_RULE} 100%)`,
            borderRadius: 3, cursor: "pointer",
          }}
        />
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {SIZE_PRESETS.map((p) => {
          const active = Math.abs(p.value - value) < 200
          return (
            <button
              type="button"
              key={p.value}
              onClick={() => onChange(p.value)}
              aria-pressed={active}
              style={{
                flex: "1 1 140px",
                padding: "10px 12px",
                background: active ? COST_INK : "#fff",
                color: active ? "#fff" : COST_INK,
                border: `1px solid ${active ? COST_INK : COST_RULE}`,
                borderRadius: 8,
                boxShadow: active ? `0 0 0 3px ${COST_ACCENT}26` : "none",
                cursor: "pointer",
                textAlign: "left",
                transition: "background-color 160ms, border-color 160ms, color 160ms, box-shadow 160ms",
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>{p.label}</div>
              <div style={{ fontSize: 12, fontVariantNumeric: "tabular-nums", color: active ? "rgba(255,255,255,0.75)" : COST_MUTED }}>
                {p.value.toLocaleString()} · {p.note}
              </div>
            </button>
          )
        })}
      </div>

      <div style={{ marginTop: 10, fontSize: 12, color: COST_MUTED }}>
        Don't know yours?{" "}
        <a href="/tools/lawn-size-calculator" style={{ color: COST_ACCENT, textDecoration: "underline", textUnderlineOffset: 3 }}>
          Trace it on a satellite map
        </a>{" "}
        (takes 20 seconds).
      </div>
    </div>
  )
}

// ── ZIP field ──────────────────────────────────────────────────────────
const ZIP_FIELD_ID = "cost-form-zip"

function ZipField({
  value,
  onChange,
  region,
  error,
}: {
  value: string
  onChange: (v: string) => void
  region: string
  error?: string
}) {
  const known = value.length === 5 && region !== "Unknown"
  return (
    <div id={ZIP_FIELD_ID} style={{ scrollMarginTop: 100 }}>
      <Label step={2} accent hint={known ? `climate zone matched: ${region}` : "required, sets your local pricing"}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <IcoPin size={13} /> ZIP code
        </span>
      </Label>
      <ZipCodeInput
        id={ZIP_FIELD_ID + "-input"}
        value={value}
        onChange={onChange}
        error={error}
        showLabel={false}
        enableGeolocation
        rememberLastZip
      />
    </div>
  )
}

// ── Grass field ────────────────────────────────────────────────────────
function GrassField({
  season,
  setSeason,
  grass,
  setGrass,
  zipKnown,
}: {
  season: Season
  setSeason: (s: Season) => void
  grass: string
  setGrass: (g: string) => void
  zipKnown: boolean
}) {
  const list = GRASSES_BY_SEASON[season]
  const active = SEASONS.find((s) => s.id === season)!
  return (
    <div>
      <Label step={3} hint={zipKnown ? `recommended for your area: ${active.recommend}` : undefined}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <IcoLeaf size={13} /> Grass
        </span>
      </Label>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          background: "#fff",
          border: `1px solid ${COST_RULE}`,
          borderRadius: 10,
          padding: 4,
          gap: 4,
        }}
      >
        {SEASONS.map((s) => {
          const isActive = s.id === season
          return (
            <button
              type="button"
              key={s.id}
              onClick={() => {
                setSeason(s.id)
                setGrass(s.recommend)
              }}
              aria-pressed={isActive}
              style={{
                padding: "10px 8px",
                background: isActive ? COST_INK : "transparent",
                color: isActive ? "#fff" : COST_INK,
                border: "none",
                borderRadius: 7,
                cursor: "pointer",
                textAlign: "center",
                transition: "background-color 160ms, border-color 160ms, color 160ms, box-shadow 160ms",
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 600 }}>{s.label}</div>
              <div style={{ fontSize: 10.5, opacity: isActive ? 0.7 : 0.55, marginTop: 2 }}>{s.note}</div>
            </button>
          )
        })}
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 12, alignItems: "center" }}>
        {list.map((g) => {
          const isActive = g === grass
          return (
            <button
              type="button"
              key={g}
              onClick={() => setGrass(g)}
              aria-pressed={isActive}
              style={{
                padding: "8px 12px",
                background: isActive ? COST_INK : "#fff",
                color: isActive ? "#fff" : COST_INK,
                border: `1px solid ${isActive ? COST_INK : COST_RULE}`,
                borderRadius: 999,
                fontSize: 13, fontWeight: 500,
                cursor: "pointer",
                transition: "background-color 160ms, border-color 160ms, color 160ms, box-shadow 160ms",
                whiteSpace: "nowrap",
              }}
            >
              {grassDisplayName(g)}
            </button>
          )
        })}
      </div>
    </div>
  )
}

// ── Service level field ────────────────────────────────────────────────
function LevelField({ value, onChange }: { value: Level; onChange: (v: Level) => void }) {
  return (
    <div>
      <Label step={4} hint={LEVELS.find((l) => l.id === value)?.pitch}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <IcoCog size={13} /> Service level
        </span>
      </Label>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
        {LEVELS.map((l) => {
          const active = l.id === value
          return (
            <button
              type="button"
              key={l.id}
              onClick={() => onChange(l.id)}
              aria-pressed={active}
              style={{
                padding: "14px 14px",
                background: active ? COST_PAPER_DEEP : "#fff",
                color: COST_INK,
                border: `1px solid ${active ? COST_INK : COST_RULE}`,
                borderRadius: 10,
                cursor: "pointer",
                textAlign: "left",
                position: "relative",
                transition: "background-color 160ms, border-color 160ms, color 160ms, box-shadow 160ms",
              }}
            >
              {l.recommended && (
                <span
                  style={{
                    position: "absolute", top: -8, right: 10,
                    background: COST_ACCENT, color: "#fff",
                    padding: "2px 8px", borderRadius: 999,
                    fontSize: 9.5, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase",
                  }}
                >
                  Most
                </span>
              )}
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>{l.name}</div>
              <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 4 }}>
                {l.incl.map((line) => (
                  <li
                    key={line}
                    style={{
                      fontSize: 11.5, color: COST_MUTED, lineHeight: 1.4,
                      display: "flex", gap: 5, alignItems: "flex-start",
                    }}
                  >
                    <span style={{ color: active ? COST_ACCENT : COST_MUTED_2, marginTop: 2 }}>·</span>
                    {line}
                  </li>
                ))}
              </ul>
            </button>
          )
        })}
      </div>
    </div>
  )
}

// ── Approach field ─────────────────────────────────────────────────────
function ApproachField({ value, onChange }: { value: Approach; onChange: (v: Approach) => void }) {
  return (
    <div>
      <Label step={5}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <IcoWrench size={13} /> Care approach
        </span>
      </Label>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
        {APPROACHES.map((a) => {
          const active = a.id === value
          return (
            <button
              type="button"
              key={a.id}
              onClick={() => onChange(a.id)}
              aria-pressed={active}
              style={{
                padding: "14px 14px",
                background: active ? COST_PAPER_DEEP : "#fff",
                color: COST_INK,
                border: `1px solid ${active ? COST_INK : COST_RULE}`,
                borderRadius: 10,
                cursor: "pointer",
                textAlign: "left",
                transition: "background-color 160ms, border-color 160ms, color 160ms, box-shadow 160ms",
              }}
            >
              <div style={{ fontSize: 14, fontWeight: 600 }}>{a.name}</div>
              <div style={{ fontSize: 12, color: COST_MUTED, marginTop: 2 }}>
                {a.meta}
              </div>
              <div style={{ fontSize: 11.5, color: COST_MUTED, marginTop: 8, lineHeight: 1.45 }}>{a.blurb}</div>
            </button>
          )
        })}
      </div>
    </div>
  )
}

// ── Inclusions ─────────────────────────────────────────────────────────
function ToggleChip({
  on,
  onClick,
  label,
  sub,
  disabled,
}: {
  on: boolean
  onClick: () => void
  label: string
  sub: string
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      aria-pressed={on}
      style={{
        flex: "1 1 220px",
        padding: "12px 14px",
        background: on ? COST_PAPER_DEEP : "#fff",
        color: disabled ? COST_MUTED_2 : COST_INK,
        border: `1px solid ${on ? COST_INK : COST_RULE}`,
        borderRadius: 10,
        cursor: disabled ? "not-allowed" : "pointer",
        textAlign: "left",
        display: "flex", alignItems: "center", gap: 12,
        opacity: disabled ? 0.55 : 1,
        transition: "background-color 160ms, border-color 160ms, color 160ms, box-shadow 160ms",
      }}
    >
      <span
        style={{
          width: 22, height: 22, borderRadius: 6, flexShrink: 0,
          border: `1.5px solid ${on ? COST_INK : COST_RULE}`,
          background: on ? COST_INK : "#fff",
          color: "#fff",
          display: "inline-flex", alignItems: "center", justifyContent: "center",
        }}
      >
        {on && <IcoCheck size={13} />}
      </span>
      <span style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 600 }}>{label}</div>
        <div style={{ fontSize: 11, color: COST_MUTED, marginTop: 1 }}>{sub}</div>
      </span>
    </button>
  )
}

function Inclusions({
  equip,
  water,
  setEquip,
  setWater,
  approach,
}: {
  equip: boolean
  water: boolean
  setEquip: (v: boolean) => void
  setWater: (v: boolean) => void
  approach: Approach
}) {
  const equipDisabled = approach === "professional"
  return (
    <div>
      <Label step={6} hint="optional add-ons">Include in estimate</Label>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <ToggleChip
          on={equip && !equipDisabled}
          disabled={equipDisabled}
          onClick={() => setEquip(!equip)}
          label="Equipment"
          sub="mower, spreader (~$120/yr)"
        />
        <ToggleChip
          on={water}
          onClick={() => setWater(!water)}
          label="Water / irrigation"
          sub="adds ~$0.04/sqft"
        />
      </div>
      {equipDisabled && (
        <div style={{ fontSize: 11.5, color: COST_MUTED_2, marginTop: 8 }}>
          Equipment is not needed when you hire a pro.
        </div>
      )}
    </div>
  )
}

// ── ZIP focus helper ──────────────────────────────────────────────────
function focusZipField() {
  if (typeof document === "undefined") return
  const wrapper = document.getElementById(ZIP_FIELD_ID)
  const input = document.getElementById(ZIP_FIELD_ID + "-input") as HTMLInputElement | null
  wrapper?.scrollIntoView({ behavior: "smooth", block: "center" })
  if (input) {
    window.setTimeout(() => input.focus({ preventScroll: true }), 280)
  }
}

// ── Live estimate panel ───────────────────────────────────────────────
function LiveStrip({
  result,
  approach,
  comparison,
  needsZip,
}: {
  result: LawnCostResult | null
  approach: Approach
  comparison: { diy: number; hybrid: number; professional: number } | null
  needsZip: boolean
}) {
  const annual = result ? Math.round(result.annualTotal) : null
  const monthly = result ? Math.round(result.monthlyTotal) : null
  const perK = result ? Math.round(result.costPer1000SqFt) : null
  const hasComparison = comparison != null
  const diy = comparison ? Math.round(comparison.diy) : 0
  const hyb = comparison ? Math.round(comparison.hybrid) : 0
  const pro = comparison ? Math.round(comparison.professional) : 0
  const max = Math.max(diy, pro, hyb, 1)
  const rows: Array<{ id: Approach; label: string; v: number }> = [
    { id: "diy", label: "DIY", v: diy },
    { id: "hybrid", label: "Hybrid", v: hyb },
    { id: "professional", label: "Pro", v: pro },
  ]

  const fmt = (n: number | null, prefix = "$") => (n == null ? "—" : `${prefix}${n.toLocaleString()}`)

  return (
    <div className="flex flex-col gap-4 rounded-2xl border border-gray-200 bg-white p-5 shadow-elevation-2">
      {needsZip ? (
        <button
          type="button"
          onClick={focusZipField}
          className="flex min-h-11 flex-col gap-1.5 text-left"
        >
          <span className="text-lg font-semibold leading-tight text-gray-900">
            Enter your ZIP to see pricing
          </span>
          <span className="text-sm font-semibold text-green-700">Jump to the ZIP field</span>
        </button>
      ) : (
        <div aria-live="polite">
          <p className="text-sm font-medium text-gray-600">Annual estimate</p>
          <p className="mt-1 flex items-baseline gap-1.5">
            <span className="text-4xl font-bold leading-none tracking-tight tabular-nums text-gray-900">
              {annual == null ? "—" : `$${annual.toLocaleString()}`}
            </span>
            <span className="text-sm text-gray-600">/ yr</span>
          </p>
        </div>
      )}

      <dl className="grid gap-2.5 border-t border-gray-200 pt-4 text-sm">
        <Row k="Per month" v={fmt(monthly)} />
        <Row k="Per 1,000 sq ft" v={fmt(perK)} />
      </dl>

      <div className="flex flex-col gap-2.5">
        <h3 className="text-sm font-semibold text-gray-900">Vs other approaches</h3>
        {rows.map((r) => {
          const active = r.id === approach
          return (
            <div key={r.id} className="flex items-center gap-2.5 text-xs">
              <span className={active ? "w-12 font-semibold text-gray-900" : "w-12 text-gray-600"}>{r.label}</span>
              <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-gray-100">
                {hasComparison && (
                  <div
                    className={active ? "h-full rounded-full bg-green-600 transition-[width]" : "h-full rounded-full bg-gray-300 transition-[width]"}
                    style={{ width: `${(r.v / max) * 100}%` }}
                  />
                )}
              </div>
              <span className={active ? "w-14 text-right font-semibold tabular-nums text-gray-900" : "w-14 text-right tabular-nums text-gray-600"}>
                {hasComparison ? `$${r.v.toLocaleString()}` : "—"}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-gray-600">{k}</dt>
      <dd className="font-semibold tabular-nums text-gray-900">{v}</dd>
    </div>
  )
}

// ── Viewport hook ─────────────────────────────────────────────────────
function useIsNarrow(breakpoint = 768) {
  const [narrow, setNarrow] = useState(false)
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${breakpoint - 1}px)`)
    const onChange = () => setNarrow(mq.matches)
    onChange()
    mq.addEventListener("change", onChange)
    return () => mq.removeEventListener("change", onChange)
  }, [breakpoint])
  return narrow
}

// Find which season a grass belongs to (for URL-param seeding)
function seasonForGrass(grass: string): Season {
  for (const s of ["warm", "cool", "trans"] as Season[]) {
    if (GRASSES_BY_SEASON[s].includes(grass)) return s
  }
  return "cool"
}

// Resolve a possibly-mangled URL grass param against canonical engine names
function resolveGrassParam(raw: string | null): string | null {
  if (!raw) return null
  const normalized = raw.toLowerCase().replace(/-/g, " ").replace(/\s+/g, " ").trim()
  const match = Object.keys(GRASS_CHARACTERISTICS).find(
    (k) => k.toLowerCase().replace(/\s+/g, " ") === normalized,
  )
  return match ?? null
}

// ── The whole calculator ──────────────────────────────────────────────
export function CostForm({ initialValues, onResultChange, error }: CostFormProps) {
  const searchParams = usePrerenderSafeSearchParams()

  // Seed initial state from URL params if present, otherwise sensible defaults
  const initial = useMemo(() => {
    const sp = searchParams
    const sizeRaw = sp?.get("size")
    const sizeNum = sizeRaw ? parseInt(sizeRaw, 10) : NaN
    const zipRaw = sp?.get("zip") ?? initialValues?.zipCode ?? ""
    const grassRaw = resolveGrassParam(sp?.get("grass") ?? null) ?? initialValues?.grassType
    const levelRaw = sp?.get("level")?.toLowerCase()
    const approachRaw = sp?.get("approach")?.toLowerCase()
    const validLevel: Level | null =
      levelRaw === "basic" || levelRaw === "standard" || levelRaw === "premium" ? (levelRaw as Level) : null
    const validApproach: Approach | null =
      approachRaw === "diy" || approachRaw === "professional" || approachRaw === "hybrid"
        ? (approachRaw as Approach)
        : null
    const cleanedZip = zipRaw.replace(/[^0-9]/g, "").slice(0, 5)
    const grass = grassRaw ?? "Tall Fescue"
    return {
      sqft: !isNaN(sizeNum) && sizeNum >= 200 ? sizeNum : initialValues?.lawnSize ?? 5000,
      zip: cleanedZip,
      grass,
      season: seasonForGrass(grass),
      level: validLevel ?? ("standard" as Level),
      approach: validApproach ?? ("diy" as Approach),
    }
    // Lock to first render — URL params seed initial state only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [sqft, setSqft] = useState(initial.sqft)
  const [zip, setZip] = useState(initial.zip)
  const [season, setSeason] = useState<Season>(initial.season)
  const [grass, setGrass] = useState<string>(initial.grass)
  const [level, setLevel] = useState<Level>(initial.level)
  const [approach, setApproach] = useState<Approach>(initial.approach)
  const [equip, setEquip] = useState(false)
  const [water, setWater] = useState(false)
  const [zipError, setZipError] = useState<string | undefined>(undefined)
  const [zipTouched, setZipTouched] = useState(false)

  const region = useMemo(() => getRegionFromZipCode(zip), [zip])
  const zipKnown = zip.length === 5 && region !== "Unknown"

  const { savedInputs, saveInputs } = useSavedInputs()
  const seededGrassFromSaved = useRef(false)
  const seededSizeFromSaved = useRef(false)
  const editedRef = useRef({ zip: false, grass: false, size: false })

  useEffect(() => {
    if (initialValues?.zipCode && /^\d{5}$/.test(initialValues.zipCode) && !editedRef.current.zip && !zip) {
      setZip(initialValues.zipCode)
    }
    if (initialValues?.grassType && GRASS_CHARACTERISTICS[initialValues.grassType] && !editedRef.current.grass) {
      setGrass(initialValues.grassType)
      setSeason(seasonForGrass(initialValues.grassType))
      seededGrassFromSaved.current = true
    }
    if (initialValues?.lawnSize && !editedRef.current.size && sqft === 5000) {
      setSqft(initialValues.lawnSize)
      seededSizeFromSaved.current = true
    }
  }, [initialValues, zip, sqft])

  // Cross-site savedInputs takes precedence over zip-derived defaults — only
  // applied if URL params didn't already seed a value.
  useEffect(() => {
    if (seededGrassFromSaved.current) return
    if (initial.grass) {
      seededGrassFromSaved.current = true
      return
    }
    const saved = savedInputs.grassType
    if (saved && GRASS_CHARACTERISTICS[saved]) {
      setGrass(saved)
      seededGrassFromSaved.current = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedInputs.grassType])

  useEffect(() => {
    if (seededSizeFromSaved.current) return
    if (initial.sqft && initial.sqft !== 5000) {
      // 5000 is the form default; if the URL didn't override that, allow saved.
      seededSizeFromSaved.current = true
      return
    }
    const saved = savedInputs.lawnSize
    if (typeof saved === 'number' && saved > 0) {
      setSqft(saved)
      seededSizeFromSaved.current = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedInputs.lawnSize])

  // Region → seed default season + grass on first known ZIP.
  // If we were already seeded from URL params, suppress the auto-seed so we don't overwrite the user's deep-link.
  const [seededFromZip, setSeededFromZip] = useState(initial.zip.length === 5)
  useEffect(() => {
    if (!seededFromZip && zipKnown) {
      // REGION_TO_SEASON is keyed on the legacy region set; degrade fine-grained
      // climate sub-regions (California, Florida, etc.) to their legacy parent.
      const nextSeason = REGION_TO_SEASON[toLegacyRegion(region)] ?? "cool"
      const regionDefault = REGION_DEFAULT_GRASS[region]
      const recommended = SEASONS.find((s) => s.id === nextSeason)?.recommend
      const list = GRASSES_BY_SEASON[nextSeason]
      const nextGrass =
        regionDefault && list.includes(regionDefault)
          ? regionDefault
          : recommended ?? list[0]
      setSeason(nextSeason)
      setGrass(nextGrass)
      setSeededFromZip(true)
    }
  }, [zipKnown, region, seededFromZip])

  // Persist grass + size to localStorage so other tools prefill. The first
  // render is skipped via the *WriteReady refs so the form's initial state
  // (URL params or default) doesn't overwrite a richer saved value.
  const grassWriteReady = useRef(false)
  useEffect(() => {
    if (!grassWriteReady.current) {
      grassWriteReady.current = true
      return
    }
    if (grass && GRASS_CHARACTERISTICS[grass]) {
      saveInputs({ grassType: grass })
    }
  }, [grass, saveInputs])

  const sizeWriteReady = useRef(false)
  useEffect(() => {
    if (!sizeWriteReady.current) {
      sizeWriteReady.current = true
      return
    }
    if (sqft > 0) saveInputs({ lawnSize: sqft })
  }, [sqft, saveInputs])

  // Equipment flag is moot when hiring a pro
  useEffect(() => {
    if (approach === "professional" && equip) setEquip(false)
  }, [approach, equip])

  // Live estimate — only when we have a usable ZIP and grass
  const liveResult = useMemo<LawnCostResult | null>(() => {
    if (!zipKnown || !grass) return null
    try {
      return calculateLawnCosts(sqft, grass, zip, level, approach, equip, water, initialValues?.sunlight ?? savedInputs.sunlight)
    } catch {
      return null
    }
  }, [zipKnown, sqft, grass, zip, level, approach, equip, water, initialValues?.sunlight, savedInputs.sunlight])

  // Stream the live result up to the page so it renders the result card +
  // breakdown without a submit step (matches watering/mowing). Null until the
  // ZIP resolves to a known region.
  useEffect(() => {
    // Pass the equipment/water toggles too — they are calculation inputs the
    // result object does not carry, but analytics still reports them.
    onResultChange?.(liveResult, { includeEquipment: equip, includeWater: water })
  }, [liveResult, equip, water, onResultChange])

  // Comparison totals across all three approaches. Reuse the engine's
  // diyTotal/professionalTotal so the upper "vs other approaches" bar
  // matches the lower comparison card exactly. The user's selected approach
  // is always read from liveResult.annualTotal.
  const comparison = useMemo(() => {
    if (!liveResult) return null
    try {
      const hybridResult = approach === "hybrid"
        ? liveResult
        : calculateLawnCosts(sqft, grass!, zip, level, "hybrid", equip, water, initialValues?.sunlight ?? savedInputs.sunlight)
      const diy = approach === "diy"
        ? liveResult.annualTotal
        : (liveResult.diyTotal ?? hybridResult.diyTotal ?? 0)
      const professional = approach === "professional"
        ? liveResult.annualTotal
        : (liveResult.professionalTotal ?? hybridResult.professionalTotal ?? 0)
      return { diy, hybrid: hybridResult.annualTotal, professional }
    } catch {
      return null
    }
  }, [liveResult, approach, sqft, grass, zip, level, equip, water, initialValues?.sunlight, savedInputs.sunlight])

  const isNarrow = useIsNarrow()
  const handleZipChange = useCallback((v: string) => {
    editedRef.current.zip = true
    setZip(v)
    setZipTouched(true)
    if (v.length === 5 && getRegionFromZipCode(v) === "Unknown") {
      setZipError("That ZIP isn't recognized. Try a five-digit US ZIP.")
    } else {
      setZipError(undefined)
    }
  }, [])

  return (
    <form
      // Kept as a <form> (with no submit UI) so the postal-code input keeps its
      // accessible grouping + browser autofill context, matching the watering and
      // mowing forms. The result is live — it streams up via onResultChange.
      onSubmit={(e) => e.preventDefault()}
      className="overflow-hidden rounded-2xl border border-gray-200 shadow-elevation-2"
      style={{
        background: COST_BG,
        color: COST_INK,
        fontFamily: "var(--font-sans, 'Inter', system-ui, sans-serif)",
      }}
    >
      {/* Slider thumb styling — scoped via class */}
      <style>{`
        .cost-range:focus-visible {
          outline: 3px solid color-mix(in srgb, ${COST_ACCENT} 45%, transparent);
          outline-offset: 4px;
        }
        .cost-range::-webkit-slider-thumb {
          -webkit-appearance: none; appearance: none;
          width: 22px; height: 22px; border-radius: 50%;
          background: #fff; border: 2px solid ${COST_ACCENT};
          box-shadow: 0 1px 4px rgba(0,0,0,0.15);
          cursor: grab;
        }
        .cost-range::-moz-range-thumb {
          width: 22px; height: 22px; border-radius: 50%;
          background: #fff; border: 2px solid ${COST_ACCENT};
          cursor: grab;
        }
      `}</style>

      {/* Body. Desktop keeps the live estimate in a sticky right rail; narrow
       * widths show the same strip in flow under the fields. */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: isNarrow ? "minmax(0, 1fr)" : "minmax(0, 1fr) 280px",
          gap: isNarrow ? 24 : 32,
          padding: isNarrow ? "24px 16px" : "32px 32px 36px",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 14,
            background: COST_PAPER_DEEP,
            borderRadius: 14,
            padding: 12,
          }}
        >
          <SectionCard>
            <SizeField
              value={sqft}
              onChange={(next) => {
                editedRef.current.size = true
                setSqft(next)
              }}
            />
          </SectionCard>
          <SectionCard>
            <ZipField value={zip} onChange={handleZipChange} region={region} error={zipTouched ? zipError : undefined} />
          </SectionCard>
          <SectionCard>
            <GrassField
              season={season}
              setSeason={setSeason}
              grass={grass}
              setGrass={(next) => {
                editedRef.current.grass = true
                setGrass(next)
              }}
              zipKnown={zipKnown}
            />
          </SectionCard>
          <SectionCard>
            <LevelField value={level} onChange={setLevel} />
          </SectionCard>
          <SectionCard>
            <ApproachField value={approach} onChange={setApproach} />
          </SectionCard>
          <SectionCard>
            <Inclusions equip={equip} water={water} setEquip={setEquip} setWater={setWater} approach={approach} />
          </SectionCard>

          {error && (
            <div role="alert" style={{ padding: 12, background: "#fdf2f0", border: "1px solid #e9c4be", borderRadius: 8, fontSize: 13, color: "#7a2418" }}>
              {error}
            </div>
          )}
        </div>

        {/* Live estimate: sticky right rail on desktop, in flow on narrow */}
        <aside
          style={
            isNarrow
              ? { display: "flex", flexDirection: "column", gap: 14 }
              : { alignSelf: "flex-start", position: "sticky", top: 24, display: "flex", flexDirection: "column", gap: 14 }
          }
        >
          {/* On phones the result card sits right under the form and repeats
              these numbers, so a priced aside shrinks to the jump link. */}
          {!(isNarrow && liveResult) && (
            <LiveStrip result={liveResult} approach={approach} comparison={comparison} needsZip={!zipKnown} />
          )}
          {isNarrow && liveResult && (
            <a
              href="#results"
              className="press flex min-h-11 items-center justify-center gap-2 rounded-xl bg-action px-5 text-sm font-bold text-action-foreground no-underline hover:bg-action-hover"
            >
              See my full cost breakdown
            </a>
          )}
          <div style={{ fontSize: 11.5, color: COST_MUTED, lineHeight: 1.5, display: "flex", gap: 8 }}>
            <IcoLock size={12} />
            <span>
              {liveResult
                ? "Your full breakdown updates below as you go. Numbers based on regional pricing data, reviewed May 2026."
                : "Numbers based on regional pricing data, reviewed May 2026."}
            </span>
          </div>
        </aside>
      </div>
    </form>
  )
}
